import { from as observableFrom, Observable } from "rxjs";
import { map, mergeMap, switchMap } from "rxjs/operators";
import { SocketService } from "../socket.service";
import { InitService } from "@core-services/init.service";
import { TradableWalletService } from "./tradable_wallet.service";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { Index } from "@models/asset/index/index.model";
import { IndexService } from "../currency/index.service";
import { parseList } from "@helpers/functions";
import { Injectable } from "@angular/core";
import { ServiceNames } from "@models/service_enums";
import { IndexTransactionNotifyService } from "../notify/index_transaction.notify.service";
import { INDEX_TRANSACTIONS_ROOM, INDEX_WALLETS_ROOM } from "@helpers/constants";
import { IndexTransaction } from "@models/transaction";
import { GetPendingTradesGQLService } from "@gql/offer";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { Trade } from "@models";

const name = ServiceNames.INDEX_WALLET;

@Injectable({
	providedIn: "root"
})
export class IndexWalletService extends TradableWalletService<IndexWallet, IndexTransaction, Index> {
	public name: string = ServiceNames.INDEX_WALLET;
	public TxType: typeof IndexTransaction = IndexTransaction;
	public WalletType: typeof IndexWallet = IndexWallet;
	protected transactionSocketRoomName: string = INDEX_TRANSACTIONS_ROOM;
	protected walletSocketRoomName: string = INDEX_WALLETS_ROOM;

	protected constructor(
		protected apicallService: ApicallService,
		protected currencyService: IndexService,
		protected destructor: DestructorService,
		protected infoService: InfoService,
		protected initService: InitService,
		protected socketService: SocketService,
		protected transactionNotifyService: IndexTransactionNotifyService,
		protected translationService: TranslationService,
		protected getPendingTradesGQLService: GetPendingTradesGQLService
	) {
		super(initService, destructor, name, getPendingTradesGQLService);
	}

	public async cancel(t: IndexTransaction): Promise<IndexTransaction> {
		const oldTransaction = new IndexTransaction();
		Object.assign(oldTransaction, t);
		const call = this.apicallService.deleteRequest(
			"wallet/" + t.getWalletId() + "/transaction/" + t.getId(),
			new ApicallOption(IndexTransaction)
		);
		return super.cancelSub(call, t, oldTransaction).toPromise();
	}

	public getParseData(wallets): IndexWallet[] {
		return parseList(wallets["attributes"]["index"]["index"]["attributes"]["wallets"], IndexWallet);
	}

	public confirmTransaction(walletId: string, transactionId: string, code: string): Observable<IndexTransaction> {
		return this.apicallService
			.get(`wallet/${walletId}/transaction/${transactionId}/confirm/${code}`)
			.pipe(switchMap(() => this.getTransaction(walletId, transactionId)));
	}

	public cancelById(walletId: string, transactionId: string): Observable<IndexTransaction> {
		return this.getTransaction(walletId, transactionId).pipe(mergeMap((tx) => observableFrom(this.cancel(tx))));
	}

	public getTradesForIndexTransaction(walletId: string, transactionId: string): Observable<Trade[]> {
		return this.apicallService
			.get(`wallet/${walletId}/transactions/${transactionId}/trades`)
			.pipe(map((rawTrades) => parseList(rawTrades, Trade)));
	}
}
